/**
 * V2ConditionEditorDialog
 *
 * Modal dialog for editing V2 route conditions.
 * Replaces V1 ConditionEditorDialog for V2 canvas edges.
 *
 * Supports:
 * - Variable-based conditions (not V1 data paths)
 * - 12 comparison operators including containsAll/Any, exists/notExists
 * - AND/OR logic with interactive group splitting/merging
 * - Type-aware value input (select, multi-select, text, number, date)
 * - Real-time conflict detection and validation
 *
 * UX flow:
 * - Each row: variable ▼ | operator ▼ | value input | ✕
 * - AND/OR chip between rows: click to split/merge groups
 * - "+ Add condition" adds a row to the last group
 * - Save disabled while blocking conflicts or completely empty
 *
 * @module reactFlow
 */

import React, { useCallback, useMemo } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import type { RouteGuard, FlowDocument, ComparisonOperator } from '@segmently/schema-models/v2';
import {
  useV2ConditionEditor,
  type UseV2ConditionEditorParams,
  type RowConnectorLogic,
} from '@modules/v2-condition-editor/hooks/useV2ConditionEditor';
import { V2ConditionRowEditor } from './V2ConditionRowEditor';
import type { V2DataSource } from '@modules/v2-condition-editor';

// ============================================================================
// TYPES
// ============================================================================

export interface V2ConditionEditorDialogProps {
  open: boolean;
  onClose: () => void;
  sourceStepId: string;
  targetStepId: string;
  sourceStepName: string;
  targetStepName: string;
  /** Current conditions on the edge (with logic connectors) */
  currentConditions: RouteGuard[];
  /** Called with the new RouteGuard[] on Save */
  onSave: (conditions: RouteGuard[]) => void;
  /** Full FlowDocument used for variable resolution */
  flowDocument: FlowDocument;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const V2ConditionEditorDialog: React.FC<V2ConditionEditorDialogProps> = ({
  open,
  onClose,
  sourceStepId,
  targetStepId: _targetStepId,
  sourceStepName,
  targetStepName,
  currentConditions,
  onSave,
  flowDocument,
}) => {
  // Hook params are stable while dialog is open
  const hookParams = useMemo<UseV2ConditionEditorParams>(
    () => ({ flowDocument, sourceStepId, initialConditions: currentConditions }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],  // intentionally stable — editor state is local to this dialog open
  );

  const {
    groups,
    dataSources,
    getConnectorAfter,
    getOperators,
    getValueOptions,
    handleVariableChange,
    handleOperatorChange,
    handleValueChange,
    handleLogicToggle,
    addCondition,
    removeCondition,
    isValid,
    blockingConflicts,
    conflicts,
    toRouteGuards,
  } = useV2ConditionEditor(hookParams);

  const conflictRowIds = useMemo(
    () => new Set(blockingConflicts.flatMap((c) => c.rowIds)),
    [blockingConflicts],
  );

  const hasNoVariables = dataSources.length === 0;

  // Total row count (across all groups)
  const totalRows = groups.reduce((sum, g) => sum + g.conditions.length, 0);

  // Global row index for chip rendering
  let globalRowIdx = 0;

  const handleSave = useCallback(() => {
    const guards = toRouteGuards();
    onSave(guards);
    onClose();
  }, [toRouteGuards, onSave, onClose]);

  const canSave = !hasNoVariables && isValid && blockingConflicts.length === 0 && totalRows > 0;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogHeader
        title="Edit Conditions"
        onClose={onClose}
      />

      <DialogContent>
        {/* Route info */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          From <strong>{sourceStepName}</strong> → <strong>{targetStepName}</strong>
        </Typography>

        {/* No variables warning */}
        {hasNoVariables && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No variables are available for this step. Add variable bindings on predecessor screens
            to enable conditional navigation.
          </Alert>
        )}

        {/* Blocking conflict alert */}
        {blockingConflicts.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {blockingConflicts[0].message}
          </Alert>
        )}

        {/* Redundant conflict info */}
        {blockingConflicts.length === 0 &&
          conflicts.filter((c) => c.type === 'redundant').length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {conflicts.find((c) => c.type === 'redundant')!.message}
          </Alert>
        )}

        {/* Condition rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {groups.flatMap((group, groupIdx) =>
            group.conditions.map((row, rowIdx) => {
              const currentGlobalIdx = globalRowIdx++;
              const connectorBefore: RowConnectorLogic = currentGlobalIdx === 0
                ? null
                : getConnectorAfter(
                    rowIdx === 0 ? groupIdx - 1 : groupIdx,
                    rowIdx === 0 ? groups[groupIdx - 1].conditions.length - 1 : rowIdx - 1,
                  );

              const sourceVariable: V2DataSource | undefined =
                row.sourceVariable ?? dataSources.find((ds) => ds.variableId === row.key);
              const operators = sourceVariable ? getOperators(sourceVariable) : [];
              const valueOptions = sourceVariable && row.operator
                ? getValueOptions(sourceVariable, row.operator)
                : { inputType: 'text' as const };

              return (
                <V2ConditionRowEditor
                  key={`${group.id}-${row.id}`}
                  row={row}
                  rowIndex={currentGlobalIdx}
                  connectorBefore={connectorBefore}
                  dataSources={dataSources}
                  operators={operators}
                  valueOptions={valueOptions}
                  isConflict={conflictRowIds.has(row.id)}
                  onVariableChange={(varId) => handleVariableChange(groupIdx, rowIdx, varId)}
                  onOperatorChange={(op) => handleOperatorChange(groupIdx, rowIdx, op as ComparisonOperator)}
                  onValueChange={(val) => handleValueChange(groupIdx, rowIdx, val)}
                  onLogicToggle={() => {
                    // Chip represents connector BEFORE this row = connector AFTER previous row
                    if (rowIdx === 0) {
                      // First row in group → previous row is last row of previous group
                      handleLogicToggle(groupIdx - 1, groups[groupIdx - 1].conditions.length - 1);
                    } else {
                      handleLogicToggle(groupIdx, rowIdx - 1);
                    }
                  }}
                  onRemove={() => removeCondition(groupIdx, rowIdx)}
                />
              );
            })
          )}
        </Box>

        {/* Add condition button */}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addCondition(groups.length - 1)}
          disabled={hasNoVariables}
          sx={{ mt: 1.5 }}
        >
          Add condition
        </Button>

        {/* Group hint */}
        {groups.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Route passes if <strong>any one group</strong> is fully satisfied.
            Click the AND/OR chip to change grouping.
          </Typography>
        )}
      </DialogContent>

      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          variant: 'outlined',
        }}
        primaryAction={{
          label: 'Save',
          onClick: handleSave,
          disabled: !canSave,
        }}
      />
    </BaseDialog>
  );
};
